/* eslint-disable max-len */
import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ChangeEvent,
  type Dispatch,
  type SetStateAction,
} from 'react'
import type { NavigateFunction } from 'react-router-dom'
import type { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'
import {
  saveNote as saveNoteAction,
  deleteNote as deleteNoteAction,
} from '@components/pages/Notes/NoteEditor'
import type { Patient } from '@dataTypes/PatientInfo.types'
import {
  INote,
  INoteTranscriptSegment,
  NoteContextType,
} from '@dataTypes/note.type'
import { ClaimStatus } from '@dataTypes/claimFeature.types'
import type { IClinic } from '@dataTypes/clinic.type'
import { AppDispatch } from '@store/index'
import { removeNote, fetchAllNotes } from '@store/actions'
import {
  createSuccessToast,
  createFailedToast,
  createChartEntryUpdatedToast,
  isChartingNoteContext,
} from '@utils/notificationToast.util'
import type { NotificationToastData } from '@components/common/NotificationToast/NotificationToast.component'
import { formatClinicAddressForPdf } from '@utils/clinic.util'
import { downloadNoteAsPdf } from '@utils/notePdfExport.util'
import type { TranscribedSegment } from '@components/pages/Notes/TranscribedAudioPanel.component'
import type {
  NoteMutationSuccessDetail,
  PendingLeaveAction,
  PreviousPageState,
} from './notesFeature.types'
import { getBackButtonLabelForOrigin } from './notesFeature.util'

type NewNoteSeedContext = {
  contextType: NoteContextType
  contextId?: string
}

const getNoteContextItemId = (note: INote): string => {
  const direct = note.context_id?.trim()
  if (direct) return direct
  const details = note.context_details ?? {}
  const appointmentId =
    typeof details.appointment_id === 'string'
      ? details.appointment_id.trim()
      : ''
  if (appointmentId) return appointmentId
  const chartId =
    typeof details.chart_id === 'string' ? details.chart_id.trim() : ''
  if (chartId) return chartId
  const tpId = typeof details.tp_id === 'string' ? details.tp_id.trim() : ''
  return tpId
}

export type UseNotesEditorFeatureParams = {
  /** Whether the host overlay / page is visible (used to reset state on re-open). */
  isOpen: boolean
  selectedPatient: Patient | null
  selectedPatientName: string
  patientPreviousPageState: PreviousPageState | null
  notes: unknown
  notesLoading: boolean
  selectedClinic: IClinic | null
  dispatch: AppDispatch
  showToast: (toast: NotificationToastData) => void
  showLoading: (opts: { container: string }) => void
  hideLoading: (container: string) => void
  navigate: NavigateFunction
  originContextId: string | null
  originContactId: string | null
  originContextType: NoteContextType | null
  originContextName: string | null
  originOpenFirstNote: boolean
  /** When location or overlay requests opening a note by id once notes are loaded */
  syncOpenNoteId?: string | undefined
  /** With `syncOpenNoteId`, open the note in the editor instead of read-only view. */
  syncOpenNoteInEditMode?: boolean
  /** Same as Notes page: Boolean(location.state?.patientId) when route-driven */
  requiresPatientForCurrentFlow: boolean
  loadingContainerSelector: string
  /** When set, "leave" confirmations close the overlay instead of navigating */
  onOverlayClose?: () => void
  /**
   * Overlay-only: label for NoteEditor back (no router previousPage). When set, overrides
   * getBackButtonLabelForOrigin(patientPreviousPageState).
   */
  editorBackButtonLabel?: string
  /** When provided (Notes page), sidebar open state is controlled by the parent (location effects). */
  isSidebarOpen?: boolean
  setIsSidebarOpen?: Dispatch<SetStateAction<boolean>>
  /** When false, the patient sidebar stays open and cannot be collapsed (Notes page). */
  sidebarCollapsible?: boolean
  /** Optional callback fired after successful save/delete note mutations. */
  onNoteMutationSuccess?: (detail?: NoteMutationSuccessDetail) => void
  /** Synchronous save success (e.g. merge into appointment modal before refetch completes). */
  onNoteSavedImmediate?: (note: INote) => void
  /**
   * When true with `onOverlayClose`, editor Back actions return to the in-overlay notes list
   * instead of closing the overlay (side panel list mode).
   */
  overlayEditorBackReturnsToList?: boolean
  /**
   * For `CLAIMS` / `CLAIM_ITEM` overlay opens: claim UI status. When omitted, add/edit are disabled (safe default).
   * New note and edit are allowed only when this is `ClaimStatus.Rejected`.
   */
  claimStatusForNotes?: ClaimStatus
}

export type UseNotesEditorFeatureResult = {
  isSidebarOpen: boolean
  toggleSidebar: () => void
  isAddingNote: boolean
  isEditingNote: boolean
  editingNote: INote | null
  isViewingNote: boolean
  viewingNote: INote | null
  noteSearchQuery: string
  noteFilters: FilterSelection
  showTranscribedAudio: boolean
  transcriptionSegments: TranscribedSegment[]
  persistableTranscriptSegments: INoteTranscriptSegment[]
  showDeleteConfirmation: boolean
  noteToDelete: INote | null
  shouldBlockNavigation: boolean
  pendingLeave: PendingLeaveAction | null
  contextInitialNoteResolved: boolean
  backButtonLabel: string | undefined
  selectedPatientNotes: INote[]
  addNotePatientName: string
  showContextViewPrereqLoader: boolean
  showContextViewResolvedOverlay: boolean
  isContextViewBootstrapFlow: boolean
  handleNoteSearchChange: (event: ChangeEvent<HTMLInputElement>) => void
  handleNoteFilterChange: (selectedFilters: FilterSelection) => void
  handleNoteResetFilters: () => void
  handleAddNote: () => void
  handleBackToNotes: () => void
  handleSaveNote: (noteData: {
    id?: string
    title: string
    content: string
    tag: string
    patientId?: string
    contextId?: string
    transcript_segments?: INoteTranscriptSegment[] | null
  }) => Promise<INote | undefined>
  handleViewNote: (note: INote) => void
  handleBackFromView: () => void
  handleEditNote: (note?: INote | null) => void
  newNoteSeedContext: NewNoteSeedContext | null
  handleDeleteNote: (note: INote) => void
  handleConfirmDelete: () => void
  handleCancelDelete: () => void
  handleDownloadNote: (note: INote) => void
  handleDeleteNoteFromEditor: (noteId: string) => void
  handleBackFromEdit: () => void
  handleViewTranscription: () => void
  handleHideTranscription: () => void
  handleTranscriptSegmentsChange: (
    panelSegments: TranscribedSegment[],
    persistable: INoteTranscriptSegment[],
  ) => void
  handleCancelLeavePage: () => void
  handleDiscardAndLeavePage: () => void
  handleSaveAndLeavePage: () => Promise<void>
  requestOverlayClose: () => void
  onRegisterSaveAndLeave: (
    saveAndLeaveHandler: (() => Promise<boolean>) | null,
  ) => void
  onNavigationGuardChange: (shouldBlock: boolean) => void
  onInitialContextNoteResolved: () => void
  /** When true, claim-context overlay disallows new note and edit (only `Rejected` allows; see `claimStatusForNotes`). */
  claimContextNotesReadOnly: boolean
  /** Location/route bootstrap helpers (Notes page only) */
  applyRouteNotesHomeList: () => void
  applyRouteOpenComposer: (sidebarOpen: boolean) => void
}

export function useNotesEditorFeature(
  params: UseNotesEditorFeatureParams,
): UseNotesEditorFeatureResult {
  const {
    isOpen,
    selectedPatient,
    selectedPatientName,
    patientPreviousPageState,
    notes,
    notesLoading,
    selectedClinic,
    dispatch,
    showToast,
    showLoading,
    hideLoading,
    navigate,
    originContextId,
    originContactId,
    originContextType,
    originOpenFirstNote,
    syncOpenNoteId,
    syncOpenNoteInEditMode = false,
    requiresPatientForCurrentFlow,
    loadingContainerSelector,
    onOverlayClose,
    editorBackButtonLabel,
    isSidebarOpen: isSidebarOpenControlled,
    setIsSidebarOpen: setIsSidebarOpenControlled,
    sidebarCollapsible = true,
    onNoteMutationSuccess,
    onNoteSavedImmediate,
    overlayEditorBackReturnsToList = false,
    claimStatusForNotes,
  } = params

  const [internalSidebarOpen, setInternalSidebarOpen] = useState<boolean>(true)
  const rawIsSidebarOpen =
    isSidebarOpenControlled !== undefined
      ? isSidebarOpenControlled
      : internalSidebarOpen
  const rawSetSidebarOpen = setIsSidebarOpenControlled ?? setInternalSidebarOpen
  const isSidebarOpen = sidebarCollapsible ? rawIsSidebarOpen : true
  const setSidebarOpen = useCallback(
    (value: SetStateAction<boolean>): void => {
      if (!sidebarCollapsible) return
      rawSetSidebarOpen(value)
    },
    [sidebarCollapsible, rawSetSidebarOpen],
  )
  const [isAddingNote, setIsAddingNote] = useState<boolean>(false)
  const [isEditingNote, setIsEditingNote] = useState<boolean>(false)
  const [editingNote, setEditingNote] = useState<INote | null>(null)
  const [isViewingNote, setIsViewingNote] = useState<boolean>(false)
  const [viewingNote, setViewingNote] = useState<INote | null>(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false)
  const [noteToDelete, setNoteToDelete] = useState<INote | null>(null)
  const [showTranscribedAudio, setShowTranscribedAudio] =
    useState<boolean>(false)
  const [transcriptionSegments, setTranscriptionSegments] = useState<
    TranscribedSegment[]
  >([])
  const [persistableTranscriptSegments, setPersistableTranscriptSegments] =
    useState<INoteTranscriptSegment[]>([])
  const [noteSearchQuery, setNoteSearchQuery] = useState<string>('')
  const [noteFilters, setNoteFilters] = useState<FilterSelection>({
    date: [],
    tag: [],
  })
  const [contextInitialNoteResolved, setContextInitialNoteResolved] =
    useState<boolean>(false)
  const [newNoteSeedContext, setNewNoteSeedContext] =
    useState<NewNoteSeedContext | null>(null)
  const [shouldBlockNavigation, setShouldBlockNavigation] =
    useState<boolean>(false)
  const [pendingLeave, setPendingLeave] = useState<PendingLeaveAction | null>(
    null,
  )
  const pendingLeaveRef = useRef<PendingLeaveAction | null>(null)
  useEffect(() => {
    pendingLeaveRef.current = pendingLeave
  }, [pendingLeave])
  const editorSaveAndLeaveRef = useRef<(() => Promise<boolean>) | null>(null)
  const appliedOpenNoteIdRef = useRef<string | null>(null)
  const appliedOpenFirstNoteRef = useRef<boolean>(false)
  /** When contact/context changes while the overlay stays open, reset first-note latch + view state. */
  const prevNoteOriginContextKeyRef = useRef<string | null>(null)
  const selectedPatientRef = useRef<Patient | null>(null)
  useEffect(() => {
    selectedPatientRef.current = selectedPatient
  }, [selectedPatient])

  const prevIsOpenRef = useRef<boolean>(isOpen)
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current
    prevIsOpenRef.current = isOpen
    if (isOpen && !wasOpen) {
      setIsAddingNote(false)
      setIsEditingNote(false)
      setEditingNote(null)
      setIsViewingNote(false)
      setViewingNote(null)
      setShowDeleteConfirmation(false)
      setNoteToDelete(null)
      setShowTranscribedAudio(false)
      setNoteSearchQuery('')
      setNoteFilters({ date: [], tag: [] })
      setContextInitialNoteResolved(false)
      setNewNoteSeedContext(null)
      setShouldBlockNavigation(false)
      setPendingLeave(null)
      appliedOpenNoteIdRef.current = null
      appliedOpenFirstNoteRef.current = false
    }
  }, [isOpen])

  const backButtonLabel = useMemo((): string | undefined => {
    if (editorBackButtonLabel !== undefined) {
      return editorBackButtonLabel
    }
    return patientPreviousPageState
      ? getBackButtonLabelForOrigin(patientPreviousPageState)
      : undefined
  }, [editorBackButtonLabel, patientPreviousPageState])

  const claimContextNotesReadOnly = useMemo((): boolean => {
    if (
      originContextType !== NoteContextType.CLAIMS &&
      originContextType !== NoteContextType.CLAIM_ITEM
    ) {
      return false
    }
    if (claimStatusForNotes === undefined) {
      return true
    }
    return claimStatusForNotes !== ClaimStatus.Rejected
  }, [originContextType, claimStatusForNotes])

  const selectedPatientNotes = useMemo((): INote[] => {
    if (!selectedPatient || !notes || !Array.isArray(notes)) return []
    return (notes as INote[]).filter(
      (n) => n.patient_id === selectedPatient.patient_id,
    )
  }, [selectedPatient, notes])

  const firstViewCandidate = useMemo((): INote | null => {
    if (!notes || !Array.isArray(notes)) return null
    const allNotes = notes as INote[]
    const normalizedOriginContextId = originContextId?.trim() ?? ''
    const normalizedOriginContextType = (originContextType ?? '')
      .toString()
      .trim()
      .toLowerCase()
    const normalizedOriginContactId = originContactId?.trim() ?? ''

    let contextNotes: INote[]
    if (
      originContextType === NoteContextType.CONTACT &&
      originContactId != null &&
      originContactId.trim() !== ''
    ) {
      contextNotes = allNotes.filter(
        (note) =>
          note.context_type === NoteContextType.CONTACT &&
          (note.contact_id === originContactId ||
            note.context_id === originContactId),
      )
    } else if (
      originContextType === NoteContextType.CALL &&
      originContextId != null &&
      originContextId.trim() !== ''
    ) {
      contextNotes = allNotes.filter(
        (note) =>
          note.context_type === NoteContextType.CALL &&
          note.context_id === originContextId,
      )
    } else if (
      originContextType === NoteContextType.BILLING &&
      originContextId != null &&
      originContextId.trim() !== ''
    ) {
      const paymentId = originContextId.trim()
      contextNotes = allNotes.filter(
        (note) =>
          note.context_type === NoteContextType.BILLING &&
          String(note.context_id ?? '').trim() === paymentId,
      )
    } else if (
      normalizedOriginContextId !== '' &&
      normalizedOriginContextType !== ''
    ) {
      contextNotes = allNotes.filter((note) => {
        const noteContextType = (note.context_type ?? '')
          .toString()
          .trim()
          .toLowerCase()
        if (noteContextType !== normalizedOriginContextType) {
          return false
        }
        return getNoteContextItemId(note) === normalizedOriginContextId
      })
    } else if (normalizedOriginContactId !== '') {
      contextNotes = allNotes.filter((note) => {
        if (note.context_type !== NoteContextType.CONTACT) {
          return false
        }
        return (
          note.contact_id === normalizedOriginContactId ||
          note.context_id === normalizedOriginContactId
        )
      })
    } else {
      contextNotes = selectedPatientNotes
    }

    if (contextNotes.length === 0) return null

    const sortedByLatest = [...contextNotes].sort((a, b) => {
      const aTime = new Date(
        a.date_time_updated_at ?? a.date_time_created_at ?? 0,
      ).getTime()
      const bTime = new Date(
        b.date_time_updated_at ?? b.date_time_created_at ?? 0,
      ).getTime()
      return bTime - aTime
    })
    return sortedByLatest[0]
  }, [
    notes,
    originContextType,
    originContactId,
    originContextId,
    selectedPatientNotes,
  ])

  useEffect((): void => {
    const openNoteId = syncOpenNoteId?.trim()

    if (!openNoteId) {
      appliedOpenNoteIdRef.current = null
      return
    }

    if (notesLoading) return
    if (appliedOpenNoteIdRef.current === openNoteId) return

    const noteToOpen = selectedPatientNotes.find(
      (note) => note.note_id === openNoteId,
    )
    if (!noteToOpen) return

    appliedOpenNoteIdRef.current = openNoteId
    const openInEdit = syncOpenNoteInEditMode && !claimContextNotesReadOnly
    if (openInEdit) {
      setIsAddingNote(false)
      setEditingNote(noteToOpen)
      setIsEditingNote(true)
      setIsViewingNote(false)
      setViewingNote(null)
    } else {
      setViewingNote(noteToOpen)
      setIsViewingNote(true)
      setIsAddingNote(false)
      setIsEditingNote(false)
      setEditingNote(null)
      setContextInitialNoteResolved(true)
    }
    setShowTranscribedAudio(false)
    setSidebarOpen(false)
  }, [
    syncOpenNoteId,
    syncOpenNoteInEditMode,
    claimContextNotesReadOnly,
    notesLoading,
    selectedPatientNotes,
    setSidebarOpen,
  ])

  useEffect((): void => {
    if (!isOpen) {
      prevNoteOriginContextKeyRef.current = null
      return
    }
    const hasStableContextOrigin =
      Boolean(originContactId?.trim()) || Boolean(originContextId?.trim())
    const key = hasStableContextOrigin
      ? [
          originContextType ?? '',
          originContactId ?? '',
          originContextId ?? '',
        ].join('|')
      : [
          originContextType ?? '',
          originContactId ?? '',
          originContextId ?? '',
          selectedPatient?.patient_id ?? '',
        ].join('|')
    const prev = prevNoteOriginContextKeyRef.current
    if (prev === key) return
    prevNoteOriginContextKeyRef.current = key
    if (prev === null) return
    appliedOpenFirstNoteRef.current = false
    appliedOpenNoteIdRef.current = null
    setIsViewingNote(false)
    setViewingNote(null)
    setIsEditingNote(false)
    setEditingNote(null)
    setIsAddingNote(false)
    setContextInitialNoteResolved(false)
    setShowTranscribedAudio(false)
  }, [
    isOpen,
    originContextType,
    originContactId,
    originContextId,
    selectedPatient?.patient_id,
  ])

  useEffect((): void => {
    if (!originOpenFirstNote) return
    if (syncOpenNoteId?.trim()) return
    if (notesLoading) return
    if (appliedOpenFirstNoteRef.current) return
    if (isAddingNote) return
    if (isEditingNote || isViewingNote) return

    if (!firstViewCandidate) return

    appliedOpenFirstNoteRef.current = true
    setViewingNote(firstViewCandidate)
    setIsViewingNote(true)
    setContextInitialNoteResolved(true)
    setIsAddingNote(false)
    setIsEditingNote(false)
    setEditingNote(null)
    setShowTranscribedAudio(false)
    setSidebarOpen(false)
  }, [
    originOpenFirstNote,
    syncOpenNoteId,
    notesLoading,
    isAddingNote,
    isEditingNote,
    isViewingNote,
    firstViewCandidate,
    setSidebarOpen,
  ])

  const toggleSidebar = useCallback((): void => {
    if (!sidebarCollapsible) return
    setSidebarOpen((prev) => !prev)
  }, [sidebarCollapsible, setSidebarOpen])

  const handleNoteSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setNoteSearchQuery(event.target.value)
    },
    [],
  )
  const handleNoteFilterChange = useCallback(
    (selectedFilters: FilterSelection): void => {
      setNoteFilters(selectedFilters)
    },
    [],
  )
  const handleNoteResetFilters = useCallback((): void => {
    setNoteSearchQuery('')
    setNoteFilters({ date: [], tag: [] })
  }, [])

  const handleAddNote = useCallback((): void => {
    if (claimContextNotesReadOnly) {
      return
    }
    const allowContextWithoutPatient =
      (originContextType === NoteContextType.CONTACT &&
        originContactId != null &&
        originContactId.trim() !== '') ||
      (originContextType === NoteContextType.CALL &&
        originContextId != null &&
        originContextId.trim() !== '') ||
      ((originContextType === NoteContextType.CLAIMS ||
        originContextType === NoteContextType.CLAIM_ITEM) &&
        originContextId != null &&
        originContextId.trim() !== '') ||
      (originContextType === NoteContextType.APPOINTMENT &&
        originContextId != null &&
        originContextId.trim() !== '')

    if (!selectedPatient && !allowContextWithoutPatient) {
      showToast(createFailedToast('Add note', 'Please select a patient first'))
      return
    }
    setIsAddingNote(true)
    setContextInitialNoteResolved(true)
    setNewNoteSeedContext(null)
    setIsViewingNote(false)
    setViewingNote(null)
    setSidebarOpen(false)
  }, [
    claimContextNotesReadOnly,
    selectedPatient,
    originContextType,
    originContactId,
    originContextId,
    showToast,
    setSidebarOpen,
  ])

  const resetToNotesHome = useCallback((): void => {
    setIsAddingNote(false)
    setIsEditingNote(false)
    setEditingNote(null)
    setIsViewingNote(false)
    setViewingNote(null)
    setShowTranscribedAudio(false)
  }, [])

  const requestOverlayClose = useCallback((): void => {
    if (!onOverlayClose) {
      return
    }
    if (shouldBlockNavigation) {
      setPendingLeave({ kind: 'overlay' })
      return
    }
    onOverlayClose()
  }, [shouldBlockNavigation, onOverlayClose])

  const handleBackToNotes = useCallback((): void => {
    if (onOverlayClose && overlayEditorBackReturnsToList) {
      if (shouldBlockNavigation) {
        setPendingLeave({ kind: 'overlayListHome' })
        return
      }
      resetToNotesHome()
      return
    }
    if (onOverlayClose) {
      requestOverlayClose()
      return
    }
    if (
      patientPreviousPageState?.path === '/notes' &&
      selectedPatient?.patient_id
    ) {
      resetToNotesHome()
      return
    }
    if (patientPreviousPageState) {
      navigate(patientPreviousPageState.path, {
        state: { ...patientPreviousPageState },
      })
      return
    }
    resetToNotesHome()
  }, [
    navigate,
    onOverlayClose,
    overlayEditorBackReturnsToList,
    patientPreviousPageState,
    requestOverlayClose,
    resetToNotesHome,
    selectedPatient?.patient_id,
    shouldBlockNavigation,
  ])

  const handleSaveNote = useCallback(
    async (noteData: {
      id?: string
      title: string
      content: string
      tag: string
      patientId?: string
      contextId?: string
      transcript_segments?: INoteTranscriptSegment[] | null
    }): Promise<INote | undefined> => {
      if (claimContextNotesReadOnly) {
        showToast(
          createFailedToast(
            'Save note failed',
            'Notes for this claim cannot be edited.',
          ),
        )
        return undefined
      }
      const isContactOnly =
        originContextType === NoteContextType.CONTACT &&
        originContactId != null &&
        originContactId.trim() !== ''
      const isCallContext =
        originContextType === NoteContextType.CALL &&
        originContextId != null &&
        originContextId.trim() !== ''
      const isAppointmentOrClaimContext =
        (originContextType === NoteContextType.APPOINTMENT ||
          originContextType === NoteContextType.CLAIMS ||
          originContextType === NoteContextType.CLAIM_ITEM) &&
        originContextId != null &&
        originContextId.trim() !== ''
      const currentPatient = selectedPatientRef.current ?? selectedPatient
      const effectivePatientId =
        currentPatient?.patient_id ?? noteData.patientId ?? undefined
      const effectiveContextId =
        originContextId?.trim() ||
        originContactId?.trim() ||
        noteData.contextId?.trim() ||
        undefined
      const effectiveContactId = originContactId?.trim() || undefined
      const effectiveContextType =
        originContextType ??
        (noteData.tag as NoteContextType) ??
        NoteContextType.PATIENT
      if (
        !effectivePatientId &&
        !isContactOnly &&
        !isCallContext &&
        !isAppointmentOrClaimContext
      ) {
        showToast(
          createFailedToast(
            'Save note failed',
            'Please select a patient before saving.',
          ),
        )
        return undefined
      }
      showLoading({ container: loadingContainerSelector })
      try {
        const saved = await saveNoteAction(
          dispatch,
          {
            noteData: {
              ...noteData,
              id: noteData.id,
              transcript_segments:
                noteData.transcript_segments !== undefined
                  ? noteData.transcript_segments
                  : persistableTranscriptSegments.length > 0
                    ? persistableTranscriptSegments
                    : undefined,
            },
            ...(effectivePatientId
              ? {
                  patientId: effectivePatientId,
                  contactId: effectiveContactId,
                  contextId: effectiveContextId,
                }
              : isContactOnly
                ? {
                    contactId: effectiveContactId,
                    contextId: effectiveContextId,
                  }
                : isCallContext || isAppointmentOrClaimContext
                  ? {
                      contactId: effectiveContactId,
                      contextId: effectiveContextId,
                    }
                  : {}),
            contextType: effectiveContextType,
            siblingNotes: (notes as INote[] | undefined) ?? [],
          },
          {
            onSuccess: (title, message) => {
              const isChartEntryEdit =
                Boolean(noteData.id) &&
                isChartingNoteContext(effectiveContextType)
              if (isChartEntryEdit) {
                showToast(
                  createChartEntryUpdatedToast(() =>
                    navigate('/charting/history'),
                  ),
                )
                return
              }
              showToast(createSuccessToast(title, message))
            },
            onError: (title, message) =>
              showToast(createFailedToast(title, message)),
          },
        )
        if (saved) {
          onNoteSavedImmediate?.(saved)
          onNoteMutationSuccess?.({ savedNote: saved })
          setIsAddingNote(false)
          setIsEditingNote(false)
          setEditingNote(null)
          setShowTranscribedAudio(false)
          setTranscriptionSegments([])
          setPersistableTranscriptSegments([])
          setNewNoteSeedContext(null)
          const hasContext =
            (originContextId != null && originContextId.trim() !== '') ||
            (originContactId != null && originContactId.trim() !== '')
          setViewingNote(saved)
          setIsViewingNote(true)
          if (hasContext) {
            setSidebarOpen(false)
          } else {
            setSidebarOpen(true)
          }
          const patientIdForFetch =
            selectedPatientRef.current?.patient_id ??
            selectedPatient?.patient_id ??
            saved.patient_id
          if (patientIdForFetch) {
            void dispatch(fetchAllNotes({ patientId: patientIdForFetch }))
          }
        }
        return saved
      } finally {
        hideLoading(loadingContainerSelector)
      }
    },
    [
      claimContextNotesReadOnly,
      selectedPatient,
      originContextId,
      originContactId,
      originContextType,
      dispatch,
      showToast,
      showLoading,
      hideLoading,
      navigate,
      notes,
      loadingContainerSelector,
      setSidebarOpen,
      onNoteMutationSuccess,
      onNoteSavedImmediate,
      persistableTranscriptSegments,
    ],
  )

  const addNotePatientName =
    selectedPatientName ||
    (originContextType === NoteContextType.CONTACT
      ? 'Contact'
      : 'Unknown Patient')

  const isContextViewBootstrapFlow = useMemo(
    (): boolean =>
      isAddingNote &&
      originOpenFirstNote &&
      Boolean(originContextId || originContactId),
    [isAddingNote, originOpenFirstNote, originContextId, originContactId],
  )
  const isContextViewBootstrapPrereqsReady = useMemo((): boolean => {
    if (requiresPatientForCurrentFlow) {
      return selectedPatient != null && !notesLoading
    }
    return true
  }, [requiresPatientForCurrentFlow, selectedPatient, notesLoading])
  const showContextViewPrereqLoader =
    isContextViewBootstrapFlow && !isContextViewBootstrapPrereqsReady
  const showContextViewResolvedOverlay =
    isContextViewBootstrapFlow &&
    isContextViewBootstrapPrereqsReady &&
    !contextInitialNoteResolved

  useEffect(() => {
    if (!isContextViewBootstrapFlow) {
      if (originOpenFirstNote) return
      setContextInitialNoteResolved(false)
    }
  }, [isContextViewBootstrapFlow, originOpenFirstNote])

  const handleViewNote = useCallback(
    (note: INote): void => {
      setIsAddingNote(false)
      setEditingNote(null)
      setIsEditingNote(false)
      setViewingNote(note)
      setIsViewingNote(true)
      if (originContextId || originContactId) setSidebarOpen(false)
    },
    [originContextId, originContactId, setSidebarOpen],
  )

  const handleBackFromView = useCallback((): void => {
    if (onOverlayClose && overlayEditorBackReturnsToList) {
      if (shouldBlockNavigation) {
        setPendingLeave({ kind: 'overlayListHome' })
        return
      }
      resetToNotesHome()
      return
    }
    if (onOverlayClose) {
      requestOverlayClose()
      return
    }
    if (
      patientPreviousPageState?.path === '/notes' &&
      selectedPatient?.patient_id
    ) {
      resetToNotesHome()
      return
    }
    if (patientPreviousPageState) {
      navigate(patientPreviousPageState.path, {
        state: { ...patientPreviousPageState },
      })
      return
    }
    setIsViewingNote(false)
    setViewingNote(null)
  }, [
    navigate,
    onOverlayClose,
    overlayEditorBackReturnsToList,
    patientPreviousPageState,
    requestOverlayClose,
    resetToNotesHome,
    selectedPatient?.patient_id,
    shouldBlockNavigation,
  ])

  const handleEditNote = useCallback(
    (note?: INote | null): void => {
      if (claimContextNotesReadOnly) {
        return
      }
      if (note == null) {
        const sourceNote = viewingNote ?? editingNote
        if (sourceNote) {
          const sourceTypeRaw = (sourceNote.context_type ?? '')
            .toString()
            .trim()
          setNewNoteSeedContext(
            sourceTypeRaw
              ? {
                  contextType: sourceTypeRaw as NoteContextType,
                  contextId:
                    getNoteContextItemId(sourceNote).trim() || undefined,
                }
              : null,
          )
        } else {
          setNewNoteSeedContext(null)
        }
        setIsAddingNote(true)
        setContextInitialNoteResolved(true)
        setEditingNote(null)
        setIsEditingNote(false)
        setIsViewingNote(false)
        setViewingNote(null)
        setSidebarOpen(false)
        return
      }
      setIsAddingNote(false)
      setNewNoteSeedContext(null)
      setEditingNote(note)
      setIsEditingNote(true)
      setIsViewingNote(false)
      setViewingNote(null)
      setSidebarOpen(false)
    },
    [claimContextNotesReadOnly, setSidebarOpen, viewingNote, editingNote],
  )

  const handleDeleteNote = useCallback((note: INote): void => {
    setNoteToDelete(note)
    setShowDeleteConfirmation(true)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    if (!noteToDelete) return

    showLoading({
      container: loadingContainerSelector,
    })
    dispatch(removeNote(noteToDelete.note_id))
      .unwrap()
      .then(() => {
        showToast(createSuccessToast('Success', 'Note deleted successfully'))
        setShowDeleteConfirmation(false)
        setNoteToDelete(null)
        onNoteMutationSuccess?.()
      })
      .catch(() => {
        showToast(
          createFailedToast(
            'Delete note failed',
            'Failed to delete note. Please try again.',
          ),
        )
      })
      .finally(() => {
        hideLoading(loadingContainerSelector)
      })
  }, [
    noteToDelete,
    dispatch,
    showToast,
    showLoading,
    hideLoading,
    loadingContainerSelector,
    onNoteMutationSuccess,
  ])

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirmation(false)
    setNoteToDelete(null)
  }, [])

  const handleDownloadNote = useCallback(
    (note: INote): void => {
      void (async (): Promise<void> => {
        try {
          showLoading({
            container: loadingContainerSelector,
          })
          const clinicName =
            selectedClinic?.clinic_name?.trim() || 'EnamelAI Clinic'
          const clinicAddress = selectedClinic
            ? formatClinicAddressForPdf(selectedClinic)
            : ''
          await downloadNoteAsPdf({
            note,
            patientName: selectedPatientName || 'Unknown',
            clinicName,
            clinicAddress,
            clinicLogoUrl: selectedClinic?.logo_url,
          })
        } catch {
          showToast(
            createFailedToast(
              'Download note',
              'Failed to download note. Please try again.',
            ),
          )
        } finally {
          hideLoading(loadingContainerSelector)
        }
      })()
    },
    [
      selectedPatientName,
      selectedClinic,
      showToast,
      showLoading,
      hideLoading,
      loadingContainerSelector,
    ],
  )

  const handleDeleteNoteFromEditor = useCallback(
    (noteId: string): void => {
      showLoading({
        container: loadingContainerSelector,
      })
      deleteNoteAction(dispatch, noteId, {
        onSuccess: (title, message) =>
          showToast(createSuccessToast(title, message)),
        onError: (title, message) =>
          showToast(createFailedToast(title, message)),
      })
        .then(() => {
          setIsEditingNote(false)
          setEditingNote(null)
          onNoteMutationSuccess?.()
        })
        .finally(() => {
          hideLoading(loadingContainerSelector)
        })
    },
    [
      dispatch,
      showToast,
      showLoading,
      hideLoading,
      loadingContainerSelector,
      onNoteMutationSuccess,
    ],
  )

  const handleBackFromEdit = useCallback((): void => {
    if (onOverlayClose && overlayEditorBackReturnsToList) {
      if (shouldBlockNavigation) {
        setPendingLeave({ kind: 'overlayListHome' })
        return
      }
      if (editingNote) {
        setViewingNote(editingNote)
        setIsViewingNote(true)
      }
      setIsEditingNote(false)
      setEditingNote(null)
      setShowTranscribedAudio(false)
      return
    }
    if (onOverlayClose) {
      requestOverlayClose()
      return
    }
    if (patientPreviousPageState) {
      navigate(patientPreviousPageState.path, {
        state: { ...patientPreviousPageState },
      })
      return
    }
    if (editingNote) {
      setViewingNote(editingNote)
      setIsViewingNote(true)
    }
    setIsEditingNote(false)
    setEditingNote(null)
    setShowTranscribedAudio(false)
  }, [
    editingNote,
    navigate,
    onOverlayClose,
    overlayEditorBackReturnsToList,
    patientPreviousPageState,
    requestOverlayClose,
    shouldBlockNavigation,
  ])

  const handleViewTranscription = useCallback(() => {
    setShowTranscribedAudio(true)
  }, [])

  const handleHideTranscription = useCallback(() => {
    setShowTranscribedAudio(false)
  }, [])

  const handleTranscriptSegmentsChange = useCallback(
    (
      panelSegments: TranscribedSegment[],
      persistable: INoteTranscriptSegment[],
    ): void => {
      setTranscriptionSegments(panelSegments)
      setPersistableTranscriptSegments(persistable)
      if (panelSegments.length > 0) {
        setShowTranscribedAudio(true)
      }
    },
    [],
  )

  useEffect(() => {
    if (!isAddingNote && !isEditingNote) {
      setShowTranscribedAudio(false)
      setTranscriptionSegments([])
      setPersistableTranscriptSegments([])
    }
  }, [isAddingNote, isEditingNote])

  useEffect((): void | (() => void) => {
    if (!shouldBlockNavigation) return
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [shouldBlockNavigation])

  useEffect((): void | (() => void) => {
    if (!shouldBlockNavigation) return
    const handleDocumentClick = (event: MouseEvent): void => {
      if (event.defaultPrevented) return
      const mouseEvent = event as MouseEvent
      if (mouseEvent.button !== 0) return
      if (mouseEvent.metaKey || mouseEvent.ctrlKey || mouseEvent.shiftKey)
        return
      const target = event.target as Element | null
      if (!target) return
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#')) return
      if (anchor.target === '_blank') return
      event.preventDefault()
      event.stopPropagation()
      setPendingLeave({ kind: 'link', href: anchor.href })
    }

    document.addEventListener('click', handleDocumentClick, true)
    return () => {
      document.removeEventListener('click', handleDocumentClick, true)
    }
  }, [shouldBlockNavigation])

  const handleCancelLeavePage = useCallback((): void => {
    setPendingLeave(null)
  }, [])

  const handleDiscardAndLeavePage = useCallback((): void => {
    const pl = pendingLeaveRef.current
    if (!pl) return
    setShouldBlockNavigation(false)
    setPendingLeave(null)
    if (pl.kind === 'overlay') {
      onOverlayClose?.()
      return
    }
    if (pl.kind === 'overlayListHome') {
      resetToNotesHome()
      return
    }
    try {
      const url = new URL(pl.href, window.location.origin)
      const isSameOrigin = url.origin === window.location.origin
      if (isSameOrigin) {
        navigate(`${url.pathname}${url.search}${url.hash}`)
        return
      }
      window.location.assign(url.toString())
    } catch {
      window.location.assign(pl.href)
    }
  }, [navigate, onOverlayClose, resetToNotesHome])

  const handleSaveAndLeavePage = useCallback(async (): Promise<void> => {
    const saveHandler = editorSaveAndLeaveRef.current
    if (saveHandler) {
      const saved = await saveHandler()
      if (!saved) return
    }
    handleDiscardAndLeavePage()
  }, [handleDiscardAndLeavePage])

  const onRegisterSaveAndLeave = useCallback(
    (saveAndLeaveHandler: (() => Promise<boolean>) | null): void => {
      editorSaveAndLeaveRef.current = saveAndLeaveHandler
    },
    [],
  )

  const onInitialContextNoteResolved = useCallback((): void => {
    setContextInitialNoteResolved(true)
  }, [])

  const applyRouteNotesHomeList = useCallback((): void => {
    setIsAddingNote(false)
    setIsEditingNote(false)
    setEditingNote(null)
    setIsViewingNote(false)
    setViewingNote(null)
    setShowTranscribedAudio(false)
    setNewNoteSeedContext(null)
  }, [])

  const applyRouteOpenComposer = useCallback(
    (sidebarOpen: boolean): void => {
      if (claimContextNotesReadOnly) {
        return
      }
      setIsAddingNote(true)
      setSidebarOpen(sidebarOpen)
    },
    [claimContextNotesReadOnly, setSidebarOpen],
  )

  return {
    isSidebarOpen,
    toggleSidebar,
    isAddingNote,
    isEditingNote,
    editingNote,
    isViewingNote,
    viewingNote,
    noteSearchQuery,
    noteFilters,
    showTranscribedAudio,
    transcriptionSegments,
    persistableTranscriptSegments,
    showDeleteConfirmation,
    noteToDelete,
    shouldBlockNavigation,
    pendingLeave,
    contextInitialNoteResolved,
    newNoteSeedContext,
    backButtonLabel,
    selectedPatientNotes,
    addNotePatientName,
    showContextViewPrereqLoader,
    showContextViewResolvedOverlay,
    isContextViewBootstrapFlow,
    handleNoteSearchChange,
    handleNoteFilterChange,
    handleNoteResetFilters,
    handleAddNote,
    handleBackToNotes,
    handleSaveNote,
    handleViewNote,
    handleBackFromView,
    handleEditNote,
    handleDeleteNote,
    handleConfirmDelete,
    handleCancelDelete,
    handleDownloadNote,
    handleDeleteNoteFromEditor,
    handleBackFromEdit,
    handleViewTranscription,
    handleHideTranscription,
    handleTranscriptSegmentsChange,
    handleCancelLeavePage,
    handleDiscardAndLeavePage,
    handleSaveAndLeavePage,
    requestOverlayClose,
    onRegisterSaveAndLeave,
    onNavigationGuardChange: setShouldBlockNavigation,
    onInitialContextNoteResolved,
    claimContextNotesReadOnly,
    applyRouteNotesHomeList,
    applyRouteOpenComposer,
  }
}
